import { useState } from 'react'
import type { Client, CreateClientInput, UpdateClientInput } from '@workflow/contracts'
import { Button, Dropdown, Field, TextArea, TextField } from '@workflow/ui'
import { errorSentence } from '@/lib/api/client'
import { useCreateClient, useUpdateClient } from '@/lib/api/hooks'

/** The form's fields, in the order the record reads: who, where, how reached. */
const TEXT_FIELDS: readonly { key: TextFieldKey; label: string; placeholder?: string; wide?: boolean }[] = [
  { key: 'name', label: 'Name', placeholder: 'The name the studio uses for them' },
  { key: 'companyName', label: 'Company name' },
  { key: 'discordServerName', label: 'Discord server name' },
  { key: 'discordServerId', label: 'Discord server ID', placeholder: 'Digits only' },
  { key: 'contactPerson', label: 'Contact person' },
  { key: 'contactEmail', label: 'Contact email', placeholder: 'name@company.com' },
  { key: 'contactPhone', label: 'Contact phone' },
  { key: 'websiteUrl', label: 'Website' },
  { key: 'countryCode', label: 'Country code', placeholder: 'Two letters' },
  { key: 'timezone', label: 'Timezone', placeholder: 'e.g. Asia/Dhaka' },
]

type TextFieldKey =
  | 'name'
  | 'companyName'
  | 'discordServerName'
  | 'discordServerId'
  | 'contactPerson'
  | 'contactEmail'
  | 'contactPhone'
  | 'websiteUrl'
  | 'countryCode'
  | 'timezone'

interface FormState extends Record<TextFieldKey, string> {
  notes: string
  rating: string
}

const emptyForm = (): FormState => ({
  name: '',
  companyName: '',
  discordServerName: '',
  discordServerId: '',
  contactPerson: '',
  contactEmail: '',
  contactPhone: '',
  websiteUrl: '',
  countryCode: '',
  timezone: '',
  notes: '',
  rating: '',
})

const formFrom = (client: Client | null | undefined): FormState => {
  if (!client) return emptyForm()
  return {
    name: client.name,
    companyName: client.companyName ?? '',
    discordServerName: client.discordServerName ?? '',
    discordServerId: client.discordServerId ?? '',
    contactPerson: client.contactPerson ?? '',
    contactEmail: client.contactEmail ?? '',
    contactPhone: client.contactPhone ?? '',
    websiteUrl: client.websiteUrl ?? '',
    countryCode: client.countryCode ?? '',
    timezone: client.timezone ?? '',
    notes: client.notes ?? '',
    rating: client.rating === null ? '' : String(client.rating),
  }
}

/**
 * One form for both moments of a client record: creation and mid-edit.
 *
 * Validation is the server's; the failure arrives as the server's sentence,
 * in place under the button. On edit only what changed is sent — absent
 * means unchanged, an emptied field sends null, which is how a record stays
 * editable in place without a Save-everything dance.
 */
export function ClientForm({
  initial,
  onSaved,
  onCancel,
}: {
  initial?: Client | null
  onSaved?: (client: Client) => void
  onCancel?: () => void
}) {
  const [form, setForm] = useState<FormState>(() => formFrom(initial))
  const create = useCreateClient()
  const update = useUpdateClient()
  const busy = create.isPending || update.isPending
  const error = create.error ?? update.error
  const set = (key: keyof FormState, value: string) => setForm((current) => ({ ...current, [key]: value }))

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (initial) {
      const input: UpdateClientInput = {}
      const changed = (key: keyof UpdateClientInput, value: string | number | null) => {
        input[key] = value as never
      }
      for (const field of TEXT_FIELDS) {
        const current = form[field.key].trim()
        const before = initial[field.key as keyof Client] as string | null
        if (current !== (before ?? '')) changed(field.key, current === '' ? null : current)
      }
      if (form.notes.trim() !== (initial.notes ?? '')) changed('notes', form.notes.trim() === '' ? null : form.notes.trim())
      const rating = form.rating === '' ? null : Number(form.rating)
      if (rating !== initial.rating) changed('rating', rating)
      update.mutate({ id: initial.id, input }, { onSuccess: (client) => onSaved?.(client as Client) })
    } else {
      const input: CreateClientInput = { name: form.name.trim() }
      for (const field of TEXT_FIELDS.slice(1)) {
        const value = form[field.key].trim()
        if (value) input[field.key as keyof CreateClientInput] = value as never
      }
      if (form.notes.trim()) input.notes = form.notes.trim()
      create.mutate(input, { onSuccess: (client) => onSaved?.(client as Client) })
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col divide-y divide-hairline">
      <div className="grid gap-x-6 gap-y-4 py-4 sm:grid-cols-2">
        {TEXT_FIELDS.map((field) => (
          <Field
            key={field.key}
            label={field.label}
            htmlFor={`client-${field.key}`}
            className={field.key === 'name' ? 'sm:col-span-2' : undefined}
          >
            <TextField
              id={`client-${field.key}`}
              value={form[field.key]}
              placeholder={field.placeholder}
              onChange={(event) => set(field.key, event.target.value)}
            />
          </Field>
        ))}
        <Field label="Notes" htmlFor="client-notes" className="sm:col-span-2">
          <TextArea
            id="client-notes"
            value={form.notes}
            rows={3}
            placeholder="Anything the relationship needs remembered"
            onChange={(event) => set('notes', event.target.value)}
          />
        </Field>
        {initial ? (
          <Field label="Rating" htmlFor="client-rating" hint="Your 1–5 rating of this client.">
            <Dropdown<string>
              id="client-rating"
              ariaLabel="Rating"
              value={form.rating}
              options={[
                { value: '', label: 'Not set' },
                ...[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: `${n} · ${n === 5 ? 'the best work' : n === 1 ? 'barely usable' : `${n} of 5`}` })),
              ]}
              onChange={(value) => set('rating', value)}
            />
          </Field>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-3 py-4">
        <Button type="submit" variant="primary" busy={busy} disabled={!initial && !form.name.trim()}>
          {initial ? 'Save changes' : 'Add client'}
        </Button>
        {onCancel ? (
          <Button size="md" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        {error ? <p className="text-caption text-danger">{errorSentence(error)}</p> : null}
      </div>
    </form>
  )
}
